// Service Worker registration, plus the "update ready" store the
// UpdateAvailableBanner reads. One module owns both because the store is
// fed entirely by the registration's lifecycle events.
//
// The flow, end to end:
//
//   1. `initServiceWorker()` (called once from src/index.tsx, production
//      builds only) registers /sw.js and keeps asking the browser to
//      re-check it against the network: every CHECK_INTERVAL_MS and whenever
//      the tab comes back into view. Left alone, a browser only re-checks
//      about once a day.
//   2. When a deploy has shipped, the browser installs the new worker in the
//      background: it downloads the whole new precache (app shell, JS, CSS)
//      while the visitor carries on. The old worker keeps serving the page
//      throughout, so nothing the visitor is looking at changes.
//   3. The new worker then WAITS (src/sw.ts only calls skipWaiting() when
//      asked). That is the moment the update is genuinely ready:
//      `registration.waiting` is set and this store flips to `ready`, which
//      is all UpdateAvailableBanner needs to show "An update is ready" with
//      a Reload button. Nothing else happens until the visitor presses it;
//      Dismiss keeps the app quiet for the rest of the session.
//   4. Reload: `applyUpdate()` posts SKIP_WAITING to the waiting worker,
//      which activates and claims the page (clientsClaim() in sw.ts). The
//      resulting `controllerchange` is the cue to reload, and that reload is
//      served from the already-downloaded precache, so it is near instant.
//      A short fallback timer reloads anyway if the hand-off never arrives,
//      so the button can never silently do nothing.
//
// This replaces vite-plugin-pwa's `virtual:pwa-register` in autoUpdate mode
// (worker activating itself on install, at any moment) plus a separate
// /version.json poller that showed a banner BEFORE anything had downloaded,
// so its Refresh button had to trigger, watch and wait out the download
// (spinner phases, "taking longer than expected", 20-90s fallback timers).
// Registering by hand with the native API is fewer moving parts than
// workbox-window for this, and keeps every reload the visitor's decision.

const SW_URL = '/sw.js';

// How often to proactively ask the browser to re-check sw.js against the
// network while a tab stays open, in addition to the visibilitychange check.
const CHECK_INTERVAL_MS = 5 * 60 * 1000;

// After SKIP_WAITING is posted, how long to wait for the new worker's
// `controllerchange` before reloading regardless. Activation is normally
// well under a second; this only covers the hand-off going missing.
export const APPLY_FALLBACK_MS = 5_000;

export interface ServiceWorkerUpdateSnapshot {
  /** A newer build is fully downloaded; a reload is all it takes to use it. */
  ready: boolean;
  /** applyUpdate() has been called and a reload is imminent. */
  applying: boolean;
}

type Listener = () => void;

const listeners = new Set<Listener>();

// The newest worker known to be installed and waiting for the go-ahead.
let waitingWorker: ServiceWorker | null = null;
// A newer worker has already taken control of this page without the visitor
// asking (another tab pressed Reload, or the worker replaced a pre-prompt
// build, see sw.ts). The page is then running an old shell against a new
// precache, and a plain reload is all that is needed.
let takenOver = false;
// Dismiss hides the banner for the update that was showing; a further
// deploy (a different waiting worker, or a takeover) shows it again.
let dismissed = false;
let applying = false;
// window.location.reload() must only ever fire once, whichever of
// controllerchange / the fallback timer / a takeover gets there first.
let reloaded = false;

let snapshot: ServiceWorkerUpdateSnapshot = { ready: false, applying: false };

function emit(): void {
  const ready = (takenOver || waitingWorker !== null) && !dismissed;
  if (ready === snapshot.ready && applying === snapshot.applying) return;
  // A fresh object only when something changed, so useSyncExternalStore
  // callers see a stable reference otherwise.
  snapshot = { ready, applying };
  listeners.forEach((listener) => listener());
}

/** Current update state. Stable reference until something changes. */
export function getServiceWorkerUpdateSnapshot(): ServiceWorkerUpdateSnapshot {
  return snapshot;
}

/** Subscribe to state changes. Returns an unsubscribe function. */
export function subscribeToServiceWorkerUpdate(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function reloadOnce(): void {
  if (reloaded) return;
  reloaded = true;
  window.location.reload();
}

function markWaiting(worker: ServiceWorker): void {
  if (worker === waitingWorker) return;
  waitingWorker = worker;
  // A different build from the one that was dismissed.
  dismissed = false;
  emit();
}

function markTakenOver(): void {
  takenOver = true;
  // Whatever was waiting is what just took over (or has been superseded).
  waitingWorker = null;
  dismissed = false;
  if (applying) {
    // The hand-off the visitor asked for has happened: reload now, served by
    // the new worker.
    reloadOnce();
    return;
  }
  emit();
}

/** Hide the banner for the update currently on offer. */
export function dismissUpdate(): void {
  if (!snapshot.ready) return;
  dismissed = true;
  emit();
}

/**
 * The Reload button. Hands the page over to the waiting worker and reloads
 * once it has taken control; reloads straight away if a newer worker is
 * already in control. Safe to call repeatedly: only the first call acts.
 */
export function applyUpdate(): void {
  if (applying || reloaded) return;
  applying = true;
  emit();

  if (!waitingWorker) {
    // Nothing waiting to activate (takeover, or a defensive call while not
    // ready): a plain reload already serves the newest active worker.
    reloadOnce();
    return;
  }

  try {
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
  } catch {
    // A worker handle that has gone redundant can throw; the fallback
    // below still reloads, and the banner returns if there is still a
    // newer worker waiting after that.
  }
  window.setTimeout(reloadOnce, APPLY_FALLBACK_MS);
}

function watchForUpdates(registration: ServiceWorkerRegistration): void {
  // Already waiting before this page loaded: dismissed in an earlier
  // session but the app was never fully closed, or found by another tab.
  if (registration.waiting) markWaiting(registration.waiting);

  registration.addEventListener('updatefound', () => {
    const installing = registration.installing;
    if (!installing) return;
    installing.addEventListener('statechange', () => {
      // `installed` alongside an older active worker means this one is
      // waiting for its turn, i.e. an update. On a first ever install there
      // is no active worker at this point: the new one activates by itself
      // moments later and there is nothing to prompt for.
      if (installing.state === 'installed' && registration.active) {
        markWaiting(installing);
      }
    });
  });

  const check = () => {
    registration.update().catch(() => {
      // Best-effort: offline or a failed fetch just means the next check
      // tries again.
    });
  };
  window.setInterval(check, CHECK_INTERVAL_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check();
  });
}

/**
 * Registers the Service Worker and starts watching it for updates. Call
 * exactly once, from src/index.tsx. A no-op in browsers without Service
 * Worker support (the app then simply has no offline shell and every reload
 * is fresh from the network).
 */
export function initServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  const container = navigator.serviceWorker;
  // Some embedded/odd hosts expose the property with no container behind it.
  if (!container) return;

  // `controllerchange` also fires on a plain first install: a fresh tab
  // boots with no controller, the worker installs, activates and claims the
  // page (clientsClaim() in sw.ts), and controller goes null -> worker,
  // exactly like a real takeover would. So with no controller at boot the
  // FIRST controllerchange is ignored; every later one is a genuine
  // takeover. One refinement below: a shift-reloaded page also boots with
  // no controller (the browser bypasses the worker for that one navigation)
  // even though a worker is already active from an earlier visit. The
  // registration knows, once it resolves, and that always happens long
  // before a real takeover could (which needs a full install first).
  let sawController = Boolean(container.controller);
  container.addEventListener('controllerchange', () => {
    if (!sawController) {
      sawController = true;
      return;
    }
    markTakenOver();
  });

  const register = () => {
    container
      .register(SW_URL, { scope: '/' })
      .then((registration) => {
        if (registration.active) sawController = true;
        watchForUpdates(registration);
      })
      .catch(() => {
        // Registration refused (bad script, storage disabled, ...): no
        // offline shell this session, and nothing an update banner could do
        // about it.
      });
  };

  // Registering after `load` keeps the worker's install (the whole precache
  // download) from competing with the page's own first paint.
  if (document.readyState === 'complete') {
    register();
  } else {
    window.addEventListener('load', register, { once: true });
  }
}
